import React, { MouseEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useApolloClient, useMutation, useSubscription } from '@apollo/client';
import {
  Box,
  Button,
  Chip,
  Checkbox,
  ListItemText,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Popover,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { Alert, Autocomplete } from '@material-ui/lab';
import {
  CancelRounded,
  CheckCircleRounded,
  Clear,
  FilterList,
  RemoveCircleOutlineRounded,
  ViewColumn,
} from '@material-ui/icons';
import { alpha, makeStyles } from '@material-ui/core/styles';
import { listPreOpBoardPatients, setPreOpBoardStatusWithResult } from '@/graph/patients';
import Troubleshooting from '@/components/pages/kiosk/tablet/Troubleshooting';
import PreOpBoardMdaPicker from './PreOpBoardMdaPicker';
import {
  groupPreOpBoardPatients,
  filterPreOpBoardPatients,
  preOpBoardMdaOptions,
  PreOpBoardMdaOption,
  isReadyForOr,
  PreOpBoardPatient,
  PreOpBoardSort,
  PreOpBoardStatusField,
} from './preOpBoardModel';

import PreOpBoardStaffSmsDialog from './PreOpBoardStaffSmsDialog';
import { staffSmsPreview, StaffSmsPreview } from './preOpBoardStaffSms';
import {
  preOpBoardColumns,
  PreOpBoardColumnId,
  readHiddenPreOpBoardColumns,
  saveHiddenPreOpBoardColumns,
} from './preOpBoardColumns';

const centeredColumns: PreOpBoardColumnId[] = [
  'caseOrder',
  'time',
  'bay',
  'anesthesiaReady',
  'surgeonReady',
  'historyAndPhysical',
  'event',
];

type StatusValue = boolean | null;

type OptimisticStatus = {
  value: StatusValue;
  base: StatusValue;
};

type StatusMenuState = {
  anchor: HTMLElement;
  patientId: string | number;
  field: PreOpBoardStatusField;
} | null;

const graphQlStatusField: Record<PreOpBoardStatusField, string> = {
  preOpBoardAnesthesiaReady: 'ANESTHESIA_READY',
  preOpBoardSurgeonReady: 'SURGEON_READY',
  preOpBoardHistoryAndPhysicalReady: 'HISTORY_AND_PHYSICAL_READY',
};

const statusFieldLabel: Record<PreOpBoardStatusField, string> = {
  preOpBoardAnesthesiaReady: 'A-Ready',
  preOpBoardSurgeonReady: 'S-Ready',
  preOpBoardHistoryAndPhysicalReady: 'H&P',
};

const useStyles = makeStyles(theme => ({
  root: {
    height: '100vh',
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    backgroundColor: theme.palette.background.default,
    color: theme.palette.text.primary,
  },
  header: {
    padding: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    flexShrink: 0,
    backgroundColor: theme.palette.background.default,
    borderBottom: `1px solid ${theme.palette.divider}`,
    zIndex: 3,
  },
  title: {
    fontWeight: 400,
  },
  heading: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1.5),
  },
  filterPopover: {
    padding: theme.spacing(2),
    width: 320,
    maxWidth: 'calc(100vw - 32px)',
  },
  filterButton: {
    width: 40,
    height: 40,
  },
  filterOptions: {
    position: 'static',
  },
  total: {
    color: theme.palette.text.secondary,
    fontWeight: 500,
    whiteSpace: 'nowrap',
    '& strong': {
      color: theme.palette.text.primary,
      fontWeight: 600,
    },
  },
  tableContainer: {
    flex: 1,
    minHeight: 0,
    overflow: 'auto',
    margin: theme.spacing(1.5),
    width: `calc(100% - ${theme.spacing(3)}px)`,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
  },
  footer: {
    flexShrink: 0,
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(2),
  },
  table: {
    tableLayout: 'fixed',
  },
  headerCell: {
    backgroundColor: theme.palette.grey.A100,
    color: theme.palette.text.primary,
    borderBottom: `2px solid ${theme.palette.divider}`,
    fontSize: 'clamp(0.7rem, 1vw, 0.9rem)',
    fontWeight: 700,
    lineHeight: 1.15,
    padding: theme.spacing(1.25, 0.75),
    whiteSpace: 'nowrap',
  },
  sortLabel: {
    color: `${theme.palette.text.secondary} !important`,
    '&.MuiTableSortLabel-active': {
      color: `${theme.palette.primary.main} !important`,
    },
    '& .MuiTableSortLabel-icon': {
      color: `${theme.palette.primary.main} !important`,
    },
  },
  groupCell: {
    padding: theme.spacing(0.8, 1.25),
    backgroundColor: alpha(theme.palette.primary.main, 0.16),
    borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.35)}`,
    borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
  },
  groupName: {
    color: theme.palette.primary.main,
    fontWeight: 700,
    letterSpacing: '0.01em',
  },
  groupCount: {
    marginLeft: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontWeight: 500,
  },
  cell: {
    padding: theme.spacing(0.75),
    height: 56,
    fontSize: 'clamp(0.7rem, 1vw, 0.92rem)',
    borderBottom: `1px solid ${theme.palette.divider}`,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  center: {
    textAlign: 'center',
  },
  statusButton: {
    width: 46,
    height: 46,
    padding: 0,
    color: theme.palette.text.disabled,
    '&:disabled': {
      color: theme.palette.text.disabled,
    },
  },
  statusReady: {
    color: theme.palette.success.main,
  },
  statusNotReady: {
    color: theme.palette.error.main,
  },
  blankStatus: {
    fontSize: '1.2rem',
    fontWeight: 500,
  },
  menuItem: {
    minHeight: 52,
    minWidth: 190,
    gap: theme.spacing(1.25),
  },
  readyMenuIcon: { color: theme.palette.success.main },
  notReadyMenuIcon: { color: theme.palette.error.main },
  clearMenuIcon: { color: theme.palette.text.secondary },
  eventReady: {
    backgroundColor: alpha(theme.palette.success.main, 0.2),
    color: theme.palette.success.light,
    border: `1px solid ${alpha(theme.palette.success.main, 0.35)}`,
    fontWeight: 700,
  },
  eventPreOp: {
    backgroundColor: theme.palette.action.hover,
    color: theme.palette.text.secondary,
    border: `1px solid ${theme.palette.divider}`,
    fontWeight: 600,
  },
  state: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    gap: theme.spacing(2),
    color: theme.palette.text.secondary,
  },
  '@media (max-width: 1100px)': {
    header: {
      padding: theme.spacing(1.5),
    },
    tableContainer: {
      margin: theme.spacing(1),
      width: `calc(100% - ${theme.spacing(2)}px)`,
    },
    headerCell: {
      padding: theme.spacing(1, 0.5),
    },
    cell: {
      padding: theme.spacing(0.5),
      height: 50,
    },
    footer: {
      padding: theme.spacing(1.5),
    },
  },
}));

const fullValue = (value?: string | null) => value?.trim() || '';

const TextCell = ({ value, className }: { value?: string | null; className?: string }) => {
  const text = fullValue(value);
  return (
    <Tooltip title={text} disableHoverListener={!text} enterTouchDelay={300}>
      <TableCell className={className} tabIndex={text ? 0 : undefined}>
        {text}
      </TableCell>
    </Tooltip>
  );
};

const StatusIcon = ({ value, classes }: { value: StatusValue; classes: ReturnType<typeof useStyles> }) => {
  if (value === true) return <CheckCircleRounded className={classes.statusReady} fontSize="large" />;
  if (value === false) return <CancelRounded className={classes.statusNotReady} fontSize="large" />;
  return <span className={classes.blankStatus}>—</span>;
};

const statusValueLabel = (value: StatusValue) => {
  if (value === true) return 'ready';
  if (value === false) return 'not ready';
  return 'blank';
};

const PreOpBoard = () => {
  const classes = useStyles();
  const { data, loading, error } = useSubscription<{ patients: PreOpBoardPatient[] }>(listPreOpBoardPatients, {
    shouldResubscribe: true,
  });
  const [writeStatus] = useMutation(setPreOpBoardStatusWithResult);
  const client = useApolloClient();
  const mounted = useRef(true);
  const statusInFlight = useRef(new Set<string>());
  const smsSequence = useRef(0);
  const [smsQueue, setSmsQueue] = useState<{ id: number; patientId: string | number; preview: StaffSmsPreview }[]>([]);
  const sms = smsQueue[0];
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const [hiddenColumns, setHiddenColumns] = useState(readHiddenPreOpBoardColumns);
  const [columnsAnchor, setColumnsAnchor] = useState<HTMLElement | null>(null);
  const visibleColumns = preOpBoardColumns.filter(column => !hiddenColumns.includes(column.id));
  const tableMinWidth = visibleColumns.reduce((total, column) => total + column.minWidth, 0);
  const changeHiddenColumns = (columns: PreOpBoardColumnId[]) => {
    const optionalColumns = columns.filter(id => id !== 'name');
    setHiddenColumns(optionalColumns);
    saveHiddenPreOpBoardColumns(optionalColumns);
  };
  const [sort, setSort] = useState<PreOpBoardSort>({ field: 'caseOrder', direction: 'asc' });
  const [mdaFilter, setMdaFilter] = useState<PreOpBoardMdaOption | null>(null);
  const [mdaFilterAnchor, setMdaFilterAnchor] = useState<HTMLElement | null>(null);
  const [menu, setMenu] = useState<StatusMenuState>(null);
  const [lastConfirmedPatients, setLastConfirmedPatients] = useState<PreOpBoardPatient[]>([]);
  const [hasConfirmedData, setHasConfirmedData] = useState(false);
  const [optimistic, setOptimistic] = useState<Record<string, OptimisticStatus>>({});
  const [pending, setPending] = useState<Record<string, boolean>>({});
  const [failure, setFailure] = useState<string | null>(null);

  useEffect(() => {
    if (data?.patients !== undefined) {
      setLastConfirmedPatients(data.patients);
      setHasConfirmedData(true);
    }
  }, [data?.patients]);

  const patients = data?.patients ?? lastConfirmedPatients;
  const hasAuthoritativeData = data?.patients !== undefined || hasConfirmedData;
  const filteredPatients = useMemo(
    () => filterPreOpBoardPatients(patients, mdaFilter?.id ?? null),
    [patients, mdaFilter]
  );
  const groups = useMemo(() => groupPreOpBoardPatients(filteredPatients, sort), [filteredPatients, sort]);
  const mdaOptions = useMemo(() => {
    const options = preOpBoardMdaOptions(patients);
    // Keep the active selection clearable if its last patient leaves Pre-Op.
    return mdaFilter && !options.some(option => option.id === mdaFilter.id) ? [mdaFilter, ...options] : options;
  }, [patients, mdaFilter]);

  useEffect(() => {
    setOptimistic(current => {
      let changed = false;
      const next = { ...current };
      Object.entries(current).forEach(([key, optimisticStatus]) => {
        const separator = key.indexOf(':');
        const patientId = key.slice(0, separator);
        const field = key.slice(separator + 1) as PreOpBoardStatusField;
        const patient = patients.find(item => String(item.id) === patientId);
        if (!patient || (patient[field] ?? null) !== optimisticStatus.base) {
          delete next[key];
          changed = true;
        }
      });
      return changed ? next : current;
    });
  }, [patients]);

  const toggleSort = (field: PreOpBoardSort['field']) => {
    setSort(current => ({
      field,
      direction: current.field === field && current.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const openStatusMenu = (event: MouseEvent<HTMLElement>, patientId: string | number, field: PreOpBoardStatusField) => {
    setMenu({ anchor: event.currentTarget, patientId, field });
  };

  const updateStatus = async (value: StatusValue) => {
    if (!menu) return;
    const { patientId, field } = menu;
    const pendingKey = `${patientId}:${field}`;
    const patient = patients.find(item => String(item.id) === String(patientId));
    const base = patient?.[field] ?? null;
    setMenu(null);

    if (value === base || statusInFlight.current.has(pendingKey)) return;

    statusInFlight.current.add(pendingKey);
    setOptimistic(current => ({ ...current, [pendingKey]: { value, base } }));
    setPending(current => ({ ...current, [pendingKey]: true }));
    try {
      const result = await writeStatus({
        variables: { patientId, field: graphQlStatusField[field], value },
      });
      if (!mounted.current) return;
      if (!result.data?.result?.success) throw new Error('The status could not be saved.');
      setPending(current => {
        const next = { ...current };
        delete next[pendingKey];
        return next;
      });
      statusInFlight.current.delete(pendingKey);
      if (result.data.result.becameReady) {
        try {
          const response = await client.query<{ preview: StaffSmsPreview | null }>({
            query: staffSmsPreview,
            variables: { patientId },
            fetchPolicy: 'no-cache',
          });
          const preview = response.data.preview;
          if (mounted.current && preview) {
            const id = ++smsSequence.current;
            // Concurrent transitions each retain their confirmation without blocking other status cells.
            setSmsQueue(current => [...current, { id, patientId, preview }]);
          }
        } catch (_smsError) {
          if (mounted.current) setFailure('S-Ready was saved, but the staff SMS preview could not be loaded.');
        }
      }
    } catch (_error) {
      statusInFlight.current.delete(pendingKey);
      if (!mounted.current) return;
      setPending(current => {
        const next = { ...current };
        delete next[pendingKey];
        return next;
      });
      setOptimistic(current => {
        const next = { ...current };
        delete next[pendingKey];
        return next;
      });
      setFailure('Status was not saved. Tap the cell to try again.');
    }
  };

  const renderStatusCell = (patient: PreOpBoardPatient, field: PreOpBoardStatusField) => {
    const pendingKey = `${patient.id}:${field}`;
    const isPending = Boolean(pending[pendingKey]);
    const value = Object.prototype.hasOwnProperty.call(optimistic, pendingKey)
      ? optimistic[pendingKey].value
      : (patient[field] ?? null);
    const label = statusFieldLabel[field];
    return (
      <TableCell className={`${classes.cell} ${classes.center}`}>
        <IconButton
          className={classes.statusButton}
          onClick={event => openStatusMenu(event, patient.id, field)}
          disabled={isPending}
          aria-label={`${label} for ${patient.name}: ${isPending ? 'saving' : statusValueLabel(value)}`}
          aria-haspopup="menu"
          aria-expanded={menu?.patientId === patient.id && menu?.field === field}
        >
          {isPending ? <CircularProgress size={28} /> : <StatusIcon value={value} classes={classes} />}
        </IconButton>
      </TableCell>
    );
  };

  const renderPatientCell = (patient: PreOpBoardPatient, column: PreOpBoardColumnId) => {
    const centered = `${classes.cell} ${classes.center}`;
    switch (column) {
      case 'surgeon':
        return <TextCell value={patient.physician?.name} className={classes.cell} />;
      case 'caseOrder':
        return <TextCell value={patient.priority} className={centered} />;
      case 'time':
        return <TextCell value={patient.procedure?.startTimeText} className={centered} />;
      case 'name':
        return <TextCell value={patient.name} className={classes.cell} />;
      case 'bay':
        return <TextCell value={patient.bed} className={centered} />;
      case 'nurse':
        return <TextCell value={patient.preOpNurse?.name} className={classes.cell} />;
      case 'mda':
        return (
          <TableCell className={classes.cell}>
            <PreOpBoardMdaPicker
              patientId={patient.id}
              value={patient.preOpBoardAnesthesiologist}
              onError={setFailure}
            />
          </TableCell>
        );
      case 'anesthesia':
        return <TextCell value={patient.procedure?.anesthesiaType} className={classes.cell} />;
      case 'anesthesiaReady':
        return renderStatusCell(patient, 'preOpBoardAnesthesiaReady');
      case 'surgeonReady':
        return renderStatusCell(patient, 'preOpBoardSurgeonReady');
      case 'historyAndPhysical':
        return renderStatusCell(patient, 'preOpBoardHistoryAndPhysicalReady');
      case 'event':
        return (
          <TableCell className={centered}>
            <Chip
              size="small"
              label={isReadyForOr(patient) ? 'Ready for OR' : 'PreOp'}
              className={isReadyForOr(patient) ? classes.eventReady : classes.eventPreOp}
            />
          </TableCell>
        );
    }
  };

  if (loading && !hasAuthoritativeData) {
    return (
      <Box className={classes.root}>
        <Box className={classes.state}>
          <CircularProgress size={48} />
          <Typography>Loading PreOP Board…</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Box className={classes.heading}>
          <Typography variant="h1" className={classes.title}>
            PreOP Board
          </Typography>
          <Button
            variant="outlined"
            startIcon={<ViewColumn />}
            aria-haspopup="menu"
            aria-controls={columnsAnchor ? 'preop-board-columns' : undefined}
            aria-expanded={Boolean(columnsAnchor)}
            onClick={event => setColumnsAnchor(event.currentTarget)}
          >
            Columns
          </Button>
          {mdaFilter && (
            <>
              <Chip label={`MDA: ${mdaFilter.name}`} onDelete={() => setMdaFilter(null)} />
              <Button
                variant="outlined"
                size="small"
                startIcon={<Clear />}
                onClick={() => setMdaFilter(null)}
                aria-label="Clear MDA filter"
              >
                Clear filter
              </Button>
            </>
          )}
        </Box>
        <Typography variant="h6" className={classes.total}>
          {mdaFilter ? (
            <>
              Showing <strong>{filteredPatients.length}</strong> of <strong>{patients.length}</strong> cases
            </>
          ) : (
            <>
              Total Cases: <strong>{patients.length}</strong>
            </>
          )}
        </Typography>
      </Box>

      {error && (
        <Alert severity="warning">Connection interrupted. Showing the last confirmed board while reconnecting.</Alert>
      )}

      {!hasAuthoritativeData ? (
        <Box className={classes.state}>
          <Typography variant="h6">The PreOP Board could not be loaded. Reconnecting…</Typography>
        </Box>
      ) : patients.length === 0 ? (
        <Box className={classes.state}>
          <Typography variant="h6">No patients are currently in Pre-Op.</Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} elevation={0} className={classes.tableContainer}>
          <Table
            stickyHeader
            size="small"
            className={classes.table}
            style={{ minWidth: tableMinWidth }}
            aria-label="PreOP Board"
          >
            <colgroup>
              {visibleColumns.map(column => (
                <col key={column.id} style={{ width: `${(column.minWidth / tableMinWidth) * 100}%` }} />
              ))}
            </colgroup>
            <TableHead>
              <TableRow>
                {visibleColumns.map(({ id, label }) => (
                  <TableCell
                    key={id}
                    className={`${classes.headerCell} ${centeredColumns.includes(id) ? classes.center : ''}`}
                  >
                    {id === 'caseOrder' || id === 'time' ? (
                      <TableSortLabel
                        active={sort.field === id}
                        direction={sort.field === id ? sort.direction : 'asc'}
                        onClick={() => toggleSort(id)}
                        className={classes.sortLabel}
                      >
                        {label}
                      </TableSortLabel>
                    ) : (
                      label
                    )}
                    {id === 'mda' && (
                      <Tooltip title={mdaFilter ? `Filter MDA: ${mdaFilter.name}` : 'Filter by MDA'}>
                        <IconButton
                          size="small"
                          className={classes.filterButton}
                          color={mdaFilter ? 'primary' : 'inherit'}
                          aria-label="Filter by MDA"
                          aria-haspopup="dialog"
                          aria-expanded={Boolean(mdaFilterAnchor)}
                          onClick={event => setMdaFilterAnchor(event.currentTarget)}
                        >
                          <FilterList fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredPatients.length === 0 && (
                <TableRow>
                  <TableCell colSpan={visibleColumns.length} className={`${classes.cell} ${classes.center}`}>
                    No patients match the selected MDA.
                  </TableCell>
                </TableRow>
              )}
              {groups.map(group => (
                <React.Fragment key={group.key}>
                  <TableRow>
                    <TableCell colSpan={visibleColumns.length} className={classes.groupCell}>
                      <span className={classes.groupName}>{group.name}</span>
                      <span className={classes.groupCount}>
                        {group.patients.length} {group.patients.length === 1 ? 'case' : 'cases'}
                      </span>
                    </TableCell>
                  </TableRow>
                  {group.patients.map(patient => (
                    <TableRow key={patient.id} hover>
                      {visibleColumns.map(column => (
                        <React.Fragment key={column.id}>{renderPatientCell(patient, column.id)}</React.Fragment>
                      ))}
                    </TableRow>
                  ))}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Box className={classes.footer}>
        <Troubleshooting roomName="PreOP Board" />
      </Box>

      <Menu
        id="preop-board-columns"
        anchorEl={columnsAnchor}
        open={Boolean(columnsAnchor)}
        onClose={() => setColumnsAnchor(null)}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        MenuListProps={{ 'aria-label': 'PreOP Board columns', disabledItemsFocusable: true }}
      >
        <MenuItem onClick={() => changeHiddenColumns([])}>Show all</MenuItem>
        {preOpBoardColumns.map(column => {
          const visible = !hiddenColumns.includes(column.id);
          return (
            <MenuItem
              key={column.id}
              role="menuitemcheckbox"
              aria-checked={visible}
              disabled={column.id === 'name'}
              onClick={() =>
                changeHiddenColumns(
                  visible ? [...hiddenColumns, column.id] : hiddenColumns.filter(id => id !== column.id)
                )
              }
            >
              <Checkbox
                checked={visible}
                disabled={column.id === 'name'}
                tabIndex={-1}
                disableRipple
                inputProps={{ 'aria-hidden': true }}
              />
              <ListItemText primary={column.label} secondary={column.id === 'name' ? 'Required' : undefined} />
            </MenuItem>
          );
        })}
      </Menu>

      <Popover
        open={Boolean(mdaFilterAnchor)}
        anchorEl={mdaFilterAnchor}
        onClose={() => setMdaFilterAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Box className={classes.filterPopover} role="dialog" aria-label="Filter patients by MDA">
          <Autocomplete
            disablePortal
            classes={{ popperDisablePortal: classes.filterOptions }}
            options={mdaOptions}
            value={mdaFilter}
            getOptionLabel={option => option.name}
            getOptionSelected={(option, value) => option.id === value.id}
            openOnFocus
            autoHighlight
            noOptionsText="No matching MDA"
            clearText="Show all MDA"
            onChange={(_event, value) => {
              setMdaFilter(value);
              setMdaFilterAnchor(null);
            }}
            renderInput={params => (
              <TextField {...params} autoFocus label="Search MDA" placeholder="All MDA" variant="outlined" />
            )}
          />
        </Box>
      </Popover>

      <Menu
        anchorEl={menu?.anchor}
        open={Boolean(menu)}
        onClose={() => setMenu(null)}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        MenuListProps={{ 'aria-label': 'PreOP Board status options' }}
      >
        <MenuItem className={classes.menuItem} onClick={() => updateStatus(true)}>
          <CheckCircleRounded className={classes.readyMenuIcon} /> Ready
        </MenuItem>
        <MenuItem className={classes.menuItem} onClick={() => updateStatus(false)}>
          <CancelRounded className={classes.notReadyMenuIcon} /> Not ready
        </MenuItem>
        <MenuItem className={classes.menuItem} onClick={() => updateStatus(null)}>
          <RemoveCircleOutlineRounded className={classes.clearMenuIcon} /> Clear
        </MenuItem>
      </Menu>

      {sms && (
        <PreOpBoardStaffSmsDialog
          key={sms.id}
          patientId={sms.patientId}
          patientName={patients.find(patient => String(patient.id) === String(sms.patientId))?.name ?? '—'}
          preview={sms.preview}
          onClose={() => setSmsQueue(current => current.slice(1))}
        />
      )}

      <Snackbar open={Boolean(failure)} autoHideDuration={5000} onClose={() => setFailure(null)}>
        <Alert severity="error" onClose={() => setFailure(null)}>
          {failure}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PreOpBoard;
